<script lang="ts">
    export default {
        name: 'Reason'
    }
</script>

<script setup lang="ts">
import { Button } from '@/components/ui/button';
import { ArrowRight, Loader2 } from 'lucide-vue-next';
import UploadFile from '@/components/upload-file/UploadFile.vue';
import DrugField from '@/components/reason/DrugField.vue';
import PatientAcknowledgmentAndAgreement from '@/components/patient-acknowledgment-and-agreement/PatientAcknowledgmentAndAgreement.vue';
import { useReasonForm } from '@/components/reason/useReasonForm';
import { items, NOTE_GENERAL_TITLE } from '@/components/reason/reasonFormData';
import SymptomsField from '@/components/reason/SymptomsField.vue';
import NoteField from '@/components/reason/NoteField.vue';
import Items from '@/components/reason/Items.vue';
import Notice from '@/components/reason/Notice.vue';
import { computed, onMounted } from 'vue';
import { store } from '@/store/store';
import { useRouter, useRoute } from 'vue-router';
import axios from 'axios';
import { getParam } from '@/lib/utils';
import { captureException } from '@sentry/vue';

const { loader, values, onSubmit, fileUpload } = useReasonForm();
const router = useRouter();
const route = useRoute();

const atLeastOneReasonSelected = computed(() => {
    return values && values.items?.length && values.items?.length > 0;
});

const uploadFileLabel = computed(() => {
    if (values.symptoms?.includes('skin-problem')) {
        return 'Upload a photo of the affected skin area:'
    }
    return 'Attach any relevant documents or images:'
});

const noteProps = computed(() => {
    const defaultProps = {
        title: NOTE_GENERAL_TITLE,
        required: true,
        placeholder: '',
    };
    const reasonBasedProps = values.items?.length === 1 ? items.find(item => item.name == values.items?.[0])?.note : {};
    const note = { ...defaultProps, ...reasonBasedProps };
    return {
        label: note.title,
        required: note.required,
        placeholder: note.placeholder,
    };
});

onMounted(async () => {
    if (store.selectedPatient.pid) {
        // make sure that if user got here through other pages, everything works as expected
        store.appointment.pid = store.selectedPatient.pid;

        // check if user has an active appointment
        try {
            const res = await axios.post('/checkPatientActiveAppointment', { pid: store.selectedPatient.pid }, {
                timeout: 2000,
            }) as any;
            if (res.status === 'success' && !!res.data.appID) {
                router.replace({
                    name: 'existing_appointment',
                    params: { domain: getParam(route) },
                    query: { appID: res.data.appID }
                })
            }
        } catch (error) {
            captureException(error, {
                extra: {
                    pid: store.selectedPatient.pid
                }
            });
        }
    }
})
</script>

<template>
    <form @submit="onSubmit" class="space-y-10">
        <h2 class="title">What is the reason for your visit?</h2>
        <Items />
        <DrugField v-if="values.items?.includes('prescription-renewal')" />
        <SymptomsField v-if="values.items?.includes('new-symptoms')" />
        <NoteField v-if="atLeastOneReasonSelected"
            :label="noteProps.label"
            :required="noteProps.required"
            :placeholder="noteProps.placeholder" />
        <UploadFile :label="uploadFileLabel" :required="values.symptoms?.includes('skin-problem')"
            v-if="atLeastOneReasonSelected" :fileUpload="fileUpload" />
        <PatientAcknowledgmentAndAgreement v-if="atLeastOneReasonSelected" />
        <Notice v-if="false" />
        <Button size="lg" class="w-full" :disabled="loader" v-if="atLeastOneReasonSelected">
            Continue
            <Loader2 v-if="loader" class="size-4 ml-1 animate-spin" />
            <ArrowRight v-else class="ml-1" />
        </Button>
    </form>
</template>
